import { LinearStepper, StepData } from '@diligentcorp/atlas-theme-mui-lens';
import { Box } from '@mui/system';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { DrawerShell } from 'src/components/atlas/molecules/Drawer/DrawerShell';
import { inviteToCase } from 'src/components/pages/Case/actions/inviteToCase';
import { inviteToCaseModal } from 'src/components/pages/Case/Case.slice';
import { useInviteToCaseMutation } from 'src/graphql';
import { AppDispatch } from 'src/store';
import { CasePermissionsType, CaseTypeEnum } from 'src/types';
import { AccessActionEnum } from '../InviteToCaseModal/components/AccessItem/AccessItem';
import { AccessStep } from './steps/AccessStep';
import { PeopleStep } from './steps/PeopleStep';
import { SummaryStep } from './steps/SummaryStep';

export interface InviteUserDropdownOption {
  label: string;
  value: string | number;
  role?: string | null;
  site?: string | null;
  country?: string | null;
  teamMember?: boolean;
  isSuperAdmin?: boolean;
}

const DEFAULT_STATE: FormState = {
  selectedTeamMembers: [],
  note: '',
  permissions: [],
  activeStep: 0,
};

type FormState = {
  activeStep: number;
  selectedTeamMembers: InviteUserDropdownOption[];
  note: string;
  permissions: CasePermissionsType[];
};

export const InviteToCaseTeamDrawer = ({
  caseType,
  open,
  onClose,
}: {
  caseType: CaseTypeEnum;
  open: boolean;
  onClose: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const inviteToCaseMutation = useInviteToCaseMutation();
  const { availableTeamMembers, caseId } = useSelector(inviteToCaseModal);
  const [formState, setFormState] = useState(DEFAULT_STATE);
  const [buttonState, setButtonState] = useState<'default' | 'pending'>(
    'default',
  );

  const [steps, setSteps] = useState<StepData[]>([
    { label: 'People', completed: false },
    { label: 'Access', completed: false },
    { label: 'Summary', completed: false },
  ]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const handleSelectTeamMembers = (
    optionsSelected: OnChangeValue<InviteUserDropdownOption, boolean>,
  ) => {
    let filteredOptionsSelected = optionsSelected;
    if (Array.isArray(optionsSelected)) {
      filteredOptionsSelected = optionsSelected?.filter(
        (option: InviteUserDropdownOption) => !option.teamMember,
      );
    }
    setFormState((prevState) => ({
      ...prevState,
      selectedTeamMembers:
        filteredOptionsSelected as InviteUserDropdownOption[],
    }));
  };

  const selectedSuperAdmins = formState.selectedTeamMembers.filter(
    (member) => member.isSuperAdmin,
  );
  const selectedApprovers = formState.selectedTeamMembers.filter(
    (member) => !member.isSuperAdmin,
  );

  const handleDeleteUserChip = (member: InviteUserDropdownOption) => {
    const updated = formState.selectedTeamMembers.filter(
      (m) => m.value !== member.value,
    );
    handleSelectTeamMembers(updated);
  };

  const handleClose = () => {
    setFormState(DEFAULT_STATE);
    setActiveStepIndex(0);
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({ ...step, completed: false })),
    );
    onClose();
  };

  const handleAssign = () => {
    const userIds = formState.selectedTeamMembers.map(
      (member) => member.value as number,
    );
    dispatch(
      inviteToCase({
        caseId,
        userIds,
        entityPermissions: formState.permissions,
        note: formState.note,
        mutationInviteToCase: inviteToCaseMutation.mutateAsync,
        queryClient,
        setButtonState,
        handleSubmitSuccess: handleClose,
      }),
    );
  };

  const onChangeStepForward = () => {
    if (activeStepIndex === steps.length - 1) {
      handleAssign();
      return;
    }
    setSteps((prevSteps) => {
      const updatedSteps = [...prevSteps];
      updatedSteps[activeStepIndex].completed = true;
      return updatedSteps;
    });
    setActiveStepIndex((prev) => prev + 1);
  };

  const onChangeStepBackward = () => {
    if (activeStepIndex > 0) {
      setSteps((prevSteps) => {
        const updatedSteps = [...prevSteps];
        updatedSteps[activeStepIndex].completed = false;
        return updatedSteps;
      });
      setActiveStepIndex((prev) => prev - 1);
    }
  };

  const coiUpdatedPermissions = (
    isViewCasePermissions: boolean,
    isEditCasePermissions: boolean,
  ) => {
    if (caseType !== CaseTypeEnum.COI) {
      return [];
    }
    const missingPermissions = [];
    if (isViewCasePermissions) {
      missingPermissions.push(
        ...[
          'VIEW_CASE_DETAILS',
          'VIEW_REPORT_DETAILS',
          'VIEW_REPORT_PII',
          'VIEW_CASE_AUDIT_LOGS_TAB',
        ],
      );
    }
    if (isEditCasePermissions) {
      missingPermissions.push(
        ...['EDIT_CASE_DETAILS', 'EDIT_REPORT_DETAILS', 'VIEW_REPORT_PII'],
      );
    }
    return missingPermissions;
  };

  const handlePermissionsChange = (
    permissions: CasePermissionsType[],
    action: AccessActionEnum,
  ) => {
    const isViewCasePermissions = permissions.some((permission) =>
      ['VIEW_CASE_DETAILS'].includes(permission),
    );
    const isEditCasePermissions = permissions.some((permission) =>
      ['EDIT_CASE_DETAILS'].includes(permission),
    );
    const additionalPermissions = coiUpdatedPermissions(
      isViewCasePermissions,
      isEditCasePermissions,
    );

    if (action === AccessActionEnum.ADD) {
      setFormState((prevState) => ({
        ...prevState,
        permissions: Array.from(
          new Set([
            ...prevState.permissions,
            ...permissions,
            ...additionalPermissions,
          ]),
        ) as CasePermissionsType[],
      }));
    } else {
      setFormState((prevState) => {
        const afterRemoval = prevState.permissions.filter(
          (p) => !permissions.includes(p),
        );
        const stillImplied = coiUpdatedPermissions(
          afterRemoval.includes('VIEW_CASE_DETAILS' as CasePermissionsType),
          afterRemoval.includes('EDIT_CASE_DETAILS' as CasePermissionsType),
        ) as CasePermissionsType[];
        const toStrip = additionalPermissions.filter(
          (p) => !stillImplied.includes(p as CasePermissionsType),
        ) as CasePermissionsType[];
        return {
          ...prevState,
          permissions: afterRemoval.filter((p) => !toStrip.includes(p)),
        };
      });
    }
  };

  const handleEditNote = (noteValue: string) => {
    setFormState((prevState) => ({
      ...prevState,
      note: noteValue,
    }));
  };

  const handleSelectedStep = () => {
    if (activeStepIndex === 0) {
      return (
        <PeopleStep
          availableTeamMembers={availableTeamMembers}
          selectedTeamMembers={formState.selectedTeamMembers}
          handleSelectTeamMembers={handleSelectTeamMembers}
          selectedSuperAdmins={selectedSuperAdmins}
          selectedApprovers={selectedApprovers}
          handleDeleteUserChip={handleDeleteUserChip}
        />
      );
    } else if (activeStepIndex === 1) {
      return (
        <AccessStep
          permissions={formState.permissions}
          handleSetPermissions={(perms, action) => {
            handlePermissionsChange(perms, action);
          }}
          selectedSuperAdmins={selectedSuperAdmins}
          selectedApprovers={selectedApprovers}
          handleDeleteUserChip={handleDeleteUserChip}
          caseType={caseType}
        />
      );
    } else if (activeStepIndex === 2) {
      return (
        <SummaryStep
          selectedPermissions={formState.permissions}
          selectedSuperAdmins={selectedSuperAdmins}
          selectedApprovers={selectedApprovers}
          handleDeleteUserChip={handleDeleteUserChip}
          note={formState.note}
          handleNoteChange={handleEditNote}
          caseType={caseType}
        />
      );
    }
  };

  return (
    <DrawerShell
      open={open}
      title="Invite team members"
      onRequestClose={handleClose}
      onNext={onChangeStepForward}
      nextDisabled={
        buttonState === 'pending' ||
        (activeStepIndex === 0 && formState.selectedTeamMembers.length === 0) ||
        (activeStepIndex === 1 && formState.permissions.length === 0)
      }
      onBack={activeStepIndex > 0 ? onChangeStepBackward : undefined}
      nextTitle={activeStepIndex === steps.length - 1 ? 'Assign' : 'Next'}
      backTitle="Previous"
      testId="invite-to-team-drawer"
    >
      <LinearStepper steps={steps} activeStepIndex={activeStepIndex} />
      <Box>{handleSelectedStep()}</Box>
    </DrawerShell>
  );
};
